import { useTranslation } from '@splitfiesta/i18n';
import {
  FREE_GROUP_EXPENSE_WARNING_AT,
  GROUP_EXPENSE_LIMIT_ERROR_KEYS,
  type GroupExpenseLimitDto,
} from '@splitfiesta/shared';
import { Alert, Button } from '@splitfiesta/ui';

interface ExpenseLimitNoticeProps {
  limit: GroupExpenseLimitDto;
  mode: 'create' | 'edit';
  proAvailable: boolean;
  onMakePro: () => void;
}

/** The free group expense limit on the add-expense form; edits are never gated. */
export function ExpenseLimitNotice({
  limit,
  mode,
  proAvailable,
  onMakePro,
}: ExpenseLimitNoticeProps) {
  const { t } = useTranslation('expenses');

  if (mode === 'edit' || limit.remaining > FREE_GROUP_EXPENSE_WARNING_AT) {
    return null;
  }

  if (limit.remaining > 0) {
    return (
      <p
        className="mb-6 text-sm text-gray-600 dark:text-gray-400"
        data-testid="expense-limit-left"
      >
        {t('expenses:limit.left', { count: limit.remaining })}
      </p>
    );
  }

  if (limit.exempt) {
    return (
      <Alert
        type="info"
        message={t('expenses:limit.proBanner', { limit: limit.max })}
        className="mb-6"
        data-testid="expense-limit-pro-banner"
      />
    );
  }

  return (
    <Alert
      type="warning"
      message={
        <div className="flex flex-wrap items-center gap-3">
          <p className="min-w-[45%] flex-1">
            {t(GROUP_EXPENSE_LIMIT_ERROR_KEYS.reached, { limit: limit.max })}
          </p>
          {proAvailable && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={onMakePro}
              className="ml-auto min-h-11"
              data-testid="expense-limit-make-pro"
            >
              {t('expenses:limit.action')}
            </Button>
          )}
        </div>
      }
      className="mb-6"
      data-testid="expense-limit-alert"
    />
  );
}
